import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';

// 导入 Swiper 样式
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const BannerContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: #000;
`;

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;

  .swiper-pagination-bullet {
    width: 48px;
    height: 3px;
    border-radius: 1.5px;
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #fff;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 3s linear;
    }

    &-active {
      opacity: 1;
      width: 64px;
      background: rgba(255, 255, 255, 0.3);

      &:after {
        transform: scaleX(1);
      }
    }
  }

  .swiper-pagination {
    bottom: 60px !important;
    left: 120px !important;
    width: auto !important;
    display: flex;
    gap: 12px;

    @media (max-width: ${theme.breakpoints.md}) {
      left: 40px !important;
      bottom: 40px !important;
    }
  }
`;

const Slide = styled(motion.div)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  padding: 0 120px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 40px;
  }
`;

const SlideContent = styled(motion.div)`
  color: ${theme.colors.white};
  max-width: 800px;
  z-index: 2;
  position: relative;
  margin-top: -80px;
`;

const SlideTitle = styled(motion.h2)`
  font-size: 64px;
  margin-bottom: 32px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
  background: linear-gradient(to right, #fff 0%, rgba(255, 255, 255, 0.8) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 48px;
    margin-bottom: 24px;
  }
`;

const SlideDescription = styled(motion.p)`
  font-size: 22px;
  margin-bottom: 40px;
  line-height: 1.8;
  opacity: 0.95;
  font-weight: 400;
  max-width: 650px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: rgba(255, 255, 255, 0.95);

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 18px;
    margin-bottom: 32px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.75) 0%,
    rgba(0, 0, 0, 0.6) 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  z-index: 1;
`;

interface BannerSlide {
  id: number;
  title: string;
  description: string;
  image: string;
}

const slides: BannerSlide[] = [
  {
    id: 1,
    title: "智能管理系统",
    description: "提供智能化、自动化的管理解决方案",
    image: "./images/s1.png",
  },
  {
    id: 2,
    title: "高效风控管理",
    description: "多维度风险评估，实时监控，确保业务安全合规",
    image: "./images/s2.png",
  },
  {
    id: 3,
    title: "数据驱动决策",
    description: "基于大数据分析，提供精准的决策支持",
    image: "./images/s3.png",
  },
];

export const Banner: React.FC = () => {
  return (
    <BannerContainer>
      <StyledSwiper
        modules={[Autoplay, Pagination, EffectFade]}
        effect="fade"
        pagination={{ clickable: true }}
        autoplay={{
          delay: 2000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        loop
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <Slide
              style={{ backgroundImage: `url(${slide.image})` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0.8 }}
              transition={{ duration: 1 }}
            >
              <Overlay />
              <SlideContent>
                <SlideTitle
                  initial={{ y: 30, opacity: 0.8 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5, duration: 1 }}
                >
                  {slide.title}
                </SlideTitle>
                <SlideDescription
                  initial={{ y: 30, opacity: 0.8 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1, duration: 1 }}
                >
                  {slide.description}
                </SlideDescription>
              </SlideContent>
            </Slide>
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </BannerContainer>
  );
};